import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollStatus(0);
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) {
      setStatus('failed');
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setBooking(res.data);
      } else if (res.data.status === 'expired') {
        setStatus('failed');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="booking-success-page">
      <Navbar />
      <div className="h-20" />

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          {status === 'loading' && (
            <div data-testid="payment-loading">
              <Loader2 className="w-16 h-16 animate-spin text-[#D65A31] mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Processing payment...</h1>
              <p className="text-gray-500">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Booking confirmed!</h1>
              <p className="text-gray-500 mb-6">
                Your payment was successful. The host has been notified and will confirm your reservation shortly.
                A confirmation email has been sent to your inbox.
              </p>
              {booking?.amount_total && (
                <p className="text-lg font-semibold mb-6">
                  Amount paid: ${(booking.amount_total / 100).toFixed(2)} {booking.currency?.toUpperCase()}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/my-bookings">
                  <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white w-full" data-testid="view-bookings-btn">
                    View My Bookings
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full" data-testid="back-home-btn">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div data-testid="payment-failed">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Payment issue</h1>
              <p className="text-gray-500 mb-6">
                We couldn't confirm your payment. Please check your email for a confirmation or try again.
              </p>
              <Link to="/">
                <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="back-home-btn">
                  Back to Home
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BookingSuccessPage;
